import { useQueryClient } from '@tanstack/react-query'
import { useRef, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ITableConfig } from './columns'
import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IChartConfigApiModel } from '~/types/graph-builder'

import columns from './columns'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import Section from '~/components/Section'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useApps from '~/hooks/useApps'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import {
  keys as graphConfigsKeys,
  useChartConfigsListQuery,
  useDeleteChartConfigMutation,
} from '~/queries/graphs-builder'
import { isFocusedCellActions } from '~/utils/table'
import { displayToast } from '~/utils/toast'

const GraphConfigsList = (): ReactElement => {
  /** Local state */

  const { appsOptions } = useApps()

  const queryClient = useQueryClient()

  const isUserAuthorizedToManageConfigs = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.GRAPH_BUILDER.WRITE])

  const navigate = useNavigate()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [selectedConfigId, setSelectedConfigId] = useState<string>()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  /** Queries & Mutations */

  const { mutateAsync: deleteConfig } = useDeleteChartConfigMutation()

  const { data: chartConfigs } = useChartConfigsListQuery({})

  /** Handlers */

  const handleAddGraphClick = () => navigate('/analytics/ca/builder')

  const handleRowClick = (event: RowClickedEvent<IChartConfigApiModel>) => {
    !isFocusedCellActions(event) && event.data && navigate(`/analytics/ca/builder/${event.data.graph_config_id}`)
  }

  const handleEditConfig = (configId: string) => navigate(`/analytics/ca/builder/${configId}`)

  const handleDeleteConfig = (configId: string) => {
    setSelectedConfigId(configId)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfigurationConfirm = async () => {
    if (selectedConfigId) {
      try {
        await deleteConfig({ configId: selectedConfigId })
        await queryClient.invalidateQueries({ queryKey: graphConfigsKeys.all })
        setIsDeleteModalOpen(false)
        displayToast('Configuration successfully deleted', 'success')
        setSelectedConfigId(undefined)
      } catch {
        displayToast('Unable to delete the configuration', 'error')
      }
    }
  }

  const handleDeleteModalState = (isOpen: boolean) => () => setIsDeleteModalOpen(isOpen)

  const rowData = useMemo(
    () =>
      chartConfigs?.data?.map((config) => ({
        ...config,
        applications: config.applications.map((appConfig) => ({
          ...appConfig,
          app_icon_url:
            appsOptions.find(
              ({ appKey, deviceType }) => appConfig.app_key === appKey && appConfig.device_type === deviceType
            )?.url || '',
        })),
        is_all_apps: config.applications.length === 0 || config.applications.length === appsOptions.length,
      })),
    [appsOptions, chartConfigs?.data]
  )

  const context: ITableConfig = {
    handleDeleteConfig,
    handleEditConfig,
  }

  /** Render */

  return (
    <>
      <Layout className="graph-configs-list-view" withPadding>
        <ViewTitle className="layout-view-title">Chart configs</ViewTitle>
        <ContentLayout>
          <Section>
            <TableWrapper
              addButton
              addButtonDisabled={!isUserAuthorizedToManageConfigs}
              addButtonLabel="New Graph"
              autoSizeStrategy={{
                type: 'fitGridWidth',
              }}
              className="graph-configs-table with-min-height"
              columnDefs={columns}
              context={context}
              entriesName="graph-configs"
              onAddButtonClick={handleAddGraphClick}
              onRowClicked={handleRowClick}
              ref={gridRef}
              rowData={rowData}
              tableRef={tableRef}
              withPagination={false}
              withSearchBar
            />
          </Section>
        </ContentLayout>
      </Layout>
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Do you really want to delete this configuration?"
        onClose={handleDeleteModalState(false)}
        onConfirm={handleDeleteConfigurationConfirm}
      />
    </>
  )
}

export default GraphConfigsList
